import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { dayIdLog } from '../lib/dayIdLog';
import { fetchUserPreferences } from '../lib/userPreferences';
import { createPreferenceSync } from '../lib/preferenceSync';
import { onSignOut } from '../lib/signOutReset';
import { DEFAULT_GROOMING_ITEMS, GROOMING_SUB_AREAS, type GroomingItem } from '../constants/grooming';
import {
  type GroomingDay,
  sumGroomingXP,
  groomingAdherence,
} from '../utils/grooming';
import { toLocalYMD, shiftYMD } from '../utils/hydration';

/**
 * Grooming store — the Personal → Grooming daily checkoff tracker. A verbatim
 * clone of `recoveryStore`: the item list lives in
 * `user_preferences.grooming_items`, each day's checkoffs in `grooming_logs`.
 *
 * ⚠️ It mints via `sumGroomingXP` ONLY (capped adherence, ~50 XP/day like every
 * other Personal tracker).
 *
 * Note it exposes no `getStreak`, unlike every sibling tracker store: Grooming's
 * streak card was retired with its Stats view, and an unread selector is just
 * another thing to keep true.
 */

const HISTORY_DAYS = 60;

interface GroomingRow {
  log_date: string;
  done_ids: string[] | null;
  scheduled: number | null;
}

function rowToDay(row: GroomingRow): GroomingDay {
  return { date: row.log_date, doneIds: Array.isArray(row.done_ids) ? row.done_ids : [], scheduled: row.scheduled ?? 0 };
}

interface GroomingStore {
  items: GroomingItem[];
  todayDate: string;
  todayDoneIds: string[];
  todayScheduled: number;
  history: GroomingDay[];

  isLoading: boolean;
  isInitialized: boolean;
  initializedUserId: string | null;

  initialize: () => Promise<void>;
  /** Re-read today's row (the tab came back — lib/dayLogFocusRefresh). Read only. */
  refreshToday: () => Promise<void>;
  toggleDone: (id: string) => Promise<void>;
  /** Mark one done for today — idempotent, never a flip (habit ticks, finished routines, energy protocols). */
  markDone: (id: string) => Promise<void>;
  addItem: (item: Omit<GroomingItem, 'id' | 'active'>) => void;
  updateItem: (id: string, patch: Partial<Omit<GroomingItem, 'id'>>) => void;
  deleteItem: (id: string) => void;
  toggleActive: (id: string) => void;

  getGroomingXP: () => number;
  getTodayAdherence: () => { done: number; scheduled: number; pct: number };
  clearStore: () => void;
}

const activeCount = (items: GroomingItem[]) => items.filter(i => i.active).length;

function withTodayInHistory(history: GroomingDay[], today: GroomingDay): GroomingDay[] {
  const others = history.filter(d => d.date !== today.date);
  return [...others, today].sort((a, b) => a.date.localeCompare(b.date));
}

/** Today's done_ids: one id on or off in the SERVER's list (lib/dayIdLog). */
const dayLog = dayIdLog({
  table: 'grooming_logs',
  column: 'done_ids',
  what: 'grooming',
  scheduled: () => activeCount(useGroomingStore.getState().items),
  show: (date, ids) => {
    const s = useGroomingStore.getState();
    const scheduled = activeCount(s.items);
    useGroomingStore.setState({ todayDate: date, todayDoneIds: ids, todayScheduled: scheduled, history: withTodayInHistory(s.history, { date, doneIds: ids, scheduled }) });
  },
  loadedFor: () => useGroomingStore.getState().initializedUserId,
});

/**
 * The items are `user_preferences.grooming_items`, merged per item on every write
 * (lib/preferenceSync.ts) — a stale tab can no longer put back one deleted
 * elsewhere or drop one added there.
 * ⚠️ An EMPTY saved list is the user's choice: only a never-saved one (null)
 * starts from the defaults. Deleting every item used to bring them back.
 */
const itemsSync = createPreferenceSync<GroomingItem[]>({
  column: 'grooming_items',
  rule: { records: 'id' },
  decode: (saved) => (Array.isArray(saved) ? (saved as GroomingItem[]) : DEFAULT_GROOMING_ITEMS.map(i => ({ ...i }))),
  read: () => useGroomingStore.getState().items,
  adopt: (items) => applyItems(useGroomingStore.setState, useGroomingStore.getState, items),
  label: 'grooming items',
  loadedFor: () => useGroomingStore.getState().initializedUserId,
});

/** Await the pending items write (tests). */
export const flushGroomingItems = (): Promise<void> => itemsSync.flush();

export const useGroomingStore = create<GroomingStore>((set, get) => ({
  items: DEFAULT_GROOMING_ITEMS.map(i => ({ ...i })),
  todayDate: toLocalYMD(new Date()),
  todayDoneIds: [],
  todayScheduled: activeCount(DEFAULT_GROOMING_ITEMS),
  history: [],

  isLoading: false,
  isInitialized: false,
  initializedUserId: null,

  initialize: () => dayLog.load(async () => {
    const at = dayLog.generation();
    const { user } = await syncManager.safeAuthCheck();
    if (!user || at !== dayLog.generation()) return;
    if (get().initializedUserId === user.id) return;

    set({ isLoading: true });
    try {
      const prefs = await fetchUserPreferences();

      const todayStr = toLocalYMD(new Date());
      const since = shiftYMD(todayStr, -HISTORY_DAYS);
      const { data, error } = await supabase
        .from('grooming_logs')
        .select('*')
        .eq('user_id', user.id)
        .gte('log_date', since)
        .order('log_date', { ascending: true });
      // Signed out while reading: the next account must not get this one's ticks — nor its
      // list as the base (so the list is loaded only after this).
      if (at !== dayLog.generation()) return;
      if (error) throw error;
      const items = itemsSync.load(prefs?.row?.grooming_items ?? null, prefs?.row?.updated_at);

      const rows = (data ?? []) as GroomingRow[];
      const history = rows.map(rowToDay);
      const todayRow = rows.find(r => r.log_date === todayStr);
      const todayDoneIds = Array.isArray(todayRow?.done_ids) ? (todayRow!.done_ids as string[]) : [];
      const todayScheduled = activeCount(items);

      set({
        items,
        todayDate: todayStr,
        todayDoneIds,
        todayScheduled,
        history: withTodayInHistory(history, { date: todayStr, doneIds: todayDoneIds, scheduled: todayScheduled }),
        isLoading: false,
        isInitialized: true,
        initializedUserId: user.id,
      });
      dayLog.loaded(todayStr, todayDoneIds);
    } catch (err) {
      if (at !== dayLog.generation()) return; // the next account's load may be running: leave its flag and base alone
      console.error('Error initializing grooming:', err);
      itemsSync.reset(); // the list was never adopted — its base must not stand either
      set({ isLoading: false });
    }
  }),

  refreshToday: () => dayLog.refresh(),

  // ⚠️ Flips what THIS screen shows — applied to the server's list, so a stale screen can
  // only ever set the one id the user tapped (never re-send or drop the others).
  toggleDone: async (id: string) => {
    await dayLog.set(id, !get().todayDoneIds.includes(id));
  },

  markDone: async (id: string) => {
    await dayLog.set(id, true);
  },

  addItem: (item) => {
    const full: GroomingItem = {
      id: crypto.randomUUID(),
      name: item.name.trim() || 'Item',
      subAreaId: item.subAreaId || GROOMING_SUB_AREAS[0].id,
      active: true,
    };
    persistItems(set, get, [...get().items, full]);
  },

  updateItem: (id, patch) => {
    persistItems(set, get, get().items.map(i => (i.id === id ? { ...i, ...patch } : i)));
  },

  deleteItem: (id) => {
    persistItems(set, get, get().items.filter(i => i.id !== id));
  },

  toggleActive: (id) => {
    persistItems(set, get, get().items.map(i => (i.id === id ? { ...i, active: !i.active } : i)));
  },

  getGroomingXP: () => sumGroomingXP(get().history),
  getTodayAdherence: () => {
    const { todayDoneIds, todayScheduled } = get();
    return { done: todayDoneIds.length, scheduled: todayScheduled, pct: groomingAdherence(todayDoneIds.length, todayScheduled) };
  },
  clearStore: () => {
    dayLog.clear();
    itemsSync.reset();
    set({
      items: DEFAULT_GROOMING_ITEMS.map(i => ({ ...i })),
      todayDate: toLocalYMD(new Date()),
      todayDoneIds: [],
      todayScheduled: activeCount(DEFAULT_GROOMING_ITEMS),
      history: [],
      isLoading: false,
      isInitialized: false,
      initializedUserId: null,
    });
  },
}));

// The items + a refreshed today-scheduled, in one set.
function applyItems(
  set: (partial: Partial<GroomingStore>) => void,
  get: () => GroomingStore,
  items: GroomingItem[]
) {
  const scheduled = activeCount(items);
  const state = get();
  const todayDay: GroomingDay = { date: state.todayDate, doneIds: state.todayDoneIds, scheduled };
  set({ items, todayScheduled: scheduled, history: withTodayInHistory(state.history, todayDay) });
}

function persistItems(
  set: (partial: Partial<GroomingStore>) => void,
  get: () => GroomingStore,
  items: GroomingItem[]
) {
  itemsSync.recordBase(get().items);
  applyItems(set, get, items);
  itemsSync.schedule();
}

onSignOut(() => useGroomingStore.getState().clearStore());
